import { useCallback } from 'react';
import {
  consent as blotoutConsent,
  tag as blotoutTag,
  data as blotoutData,
} from '@blotoutio/edgetag-sdk-js';
import { getUser } from '@/lib/user';
import Cookies from 'js-cookie';

export const BLOTOUT_EVENTS = {
  ADD_TO_CART: 'AddToCart',
  COMPLETE_ID_VERIFICATION: 'CompleteIdVerification',
  COMPLETE_REGISTRATION: 'CompleteRegistration',
  CONTACT: 'Contact',
  INITIATE_CHECKOUT: 'InitiateCheckout',
  PAGE_VIEW: 'PageView',
  PURCHASE: 'Purchase',
};

const BLOTOUT_COOKIE_NAME = 'onyx-blotout';
const EMPTY_COOKIE: BlotoutCookie = { events: [] };

export interface BlotoutTagParams {
  event: string;
  eventData?: Record<string, unknown>;
  providers?: Record<string, unknown>;
  options?: Record<string, unknown>;
  once?: boolean;
}

interface BlotoutDataParams {
  providers?: Record<string, unknown>;
  options?: Record<string, unknown>;
}

type BlotoutCookie = {
  events: string[];
};

function eventTracked(cookie: BlotoutCookie, event: string): boolean {
  return cookie.events.includes(event);
}

function getBlotoutCookie(): BlotoutCookie {
  let cookie: BlotoutCookie;
  const rawCookie = Cookies.get(BLOTOUT_COOKIE_NAME);
  try {
    cookie = rawCookie ? JSON.parse(rawCookie) : EMPTY_COOKIE;
  } catch {
    cookie = EMPTY_COOKIE;
  }
  return cookie;
}

export default function useBlotout() {
  const data = useCallback(({ providers = {}, options = {} }: BlotoutDataParams) => {
    const { firstName, lastName, email } = getUser();

    if (firstName && lastName && email) {
      blotoutData(
        {
          firstName: firstName.trim().toLowerCase(),
          lastName: lastName.trim().toLowerCase(),
          email: email.trim().toLowerCase(),
        },
        providers,
        options,
      );
    }
  }, []);

  const consent = useCallback(
    (consentGiven: boolean) => {
      blotoutConsent({ all: consentGiven }, { all: consentGiven });
      data({ options: { method: 'beacon' } });
    },
    [data],
  );

  const tag = useCallback(
    ({ event, eventData, providers = {}, options = {}, once = true }: BlotoutTagParams) => {
      const cookie: BlotoutCookie = getBlotoutCookie();
      if (once && eventTracked(cookie, event)) return;
      if (once) {
        Cookies.set(
          BLOTOUT_COOKIE_NAME,
          JSON.stringify({ events: [...cookie.events, event] } as BlotoutCookie),
          { path: '/' },
        );
      }
      data({ providers, options });
      blotoutTag(event, eventData, providers, options);
    },
    [data],
  );

  return { consent, tag };
}
